import { useEffect, useState } from "react";
import axios from "axios";
import { API } from "../config/api";

/**
 * Shared hook — fetches today's intraday OHLCV (or a historical day if `date`
 * is provided) and returns:
 *   - byTime:   { "HH:MM" → vwap absolute price }   (5-min bucket aligned)
 *   - byTimePct:{ "HH:MM" → vwap pct from open }
 *   - latest:   final running VWAP price for the session
 *   - latestPct: final running VWAP as % from open
 *   - openPrice: today's (or selected day's) open
 *   - hasData:  true if we successfully computed a VWAP
 *
 * Used as a single source of truth for the violet VWAP overlay on every
 * price chart site-wide.
 */
export default function useTodayVwap(ticker, date) {
  const [state, setState] = useState({
    byTime: {}, byTimePct: {}, latest: null, latestPct: null,
    openPrice: null, hasData: false,
  });

  useEffect(() => {
    if (!ticker) return;
    let cancelled = false;
    (async () => {
      try {
        const url = date
          ? `${API}/stock/intraday-live/${ticker}?dot_interval=1m&date=${date}`
          : `${API}/stock/intraday-live/${ticker}?dot_interval=1m`;
        const res = await axios.get(url, { timeout: 15000 });
        if (cancelled) return;
        const candles = res.data?.chart_data || [];
        const openPrice = res.data?.open_price || candles[0]?.open || candles[0]?.price;
        if (!candles.length || !openPrice) {
          setState({ byTime: {}, byTimePct: {}, latest: null, latestPct: null,
                     openPrice: null, hasData: false });
          return;
        }
        let cumPV = 0, cumV = 0;
        const byTime = {};
        const byTimePct = {};
        let latest = null;
        for (const c of candles) {
          const high = c.high ?? c.price ?? 0;
          const low = c.low ?? c.price ?? 0;
          const close = c.close ?? c.price ?? 0;
          const vol = c.volume ?? 0;
          const tp = (high + low + close) / 3;
          if (vol > 0 && tp > 0) {
            cumPV += tp * vol;
            cumV += vol;
          }
          const vwap = cumV > 0 ? cumPV / cumV : tp;
          latest = vwap;
          const t = (c.time || "").trim();
          const m = t.match(/^(\d{1,2}):(\d{2})$/);
          if (!m) continue;
          const hh = String(parseInt(m[1], 10)).padStart(2, "0");
          const mm = parseInt(m[2], 10);
          const bucket = `${hh}:${String(Math.floor(mm / 5) * 5).padStart(2, "0")}`;
          byTime[bucket] = vwap;
          byTimePct[bucket] = ((vwap / openPrice) - 1) * 100;
        }
        setState({
          byTime, byTimePct,
          latest: latest != null ? Number(latest.toFixed(2)) : null,
          latestPct: latest != null ? Number((((latest / openPrice) - 1) * 100).toFixed(4)) : null,
          openPrice: Number(openPrice),
          hasData: true,
        });
      } catch {
        if (!cancelled) {
          setState({ byTime: {}, byTimePct: {}, latest: null, latestPct: null,
                     openPrice: null, hasData: false });
        }
      }
    })();
    return () => { cancelled = true; };
  }, [ticker, date]);

  return state;
}
